import StatusButton from 'components/atoms/StatusButton';
import React, { useState } from 'react';
import VehicleCard from 'components/molecules/VehicleCard';
import IncidentCard from 'components/molecules/IncidentCard';
import SurveyCard from 'components/molecules/SurveyCard';
import 'index.css';
import SlideDown from 'components/atoms/SlideDown';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/solid';
import { IVehicleFault } from 'types/VehicleFaults';
import { formatName } from 'utils/formatName';
import { IVehicleIncident } from 'types/VehicleIncidents';
import { IVehicleSurveySubmissions } from 'types/Surveys';

export interface IVehicleRow {
	data: IVehicleFault | IVehicleIncident | IVehicleSurveySubmissions;
}

const VehicleRow: React.FC<IVehicleRow> = ({ data }) => {
	const [open, setOpen] = useState(false);
	const [slideDownDisabled, setSlideDownDisabled] = useState(false);
	const [cellStyle, setCellStyle] = useState(
		'whitespace-nowrap px-3 py-4 text-sm text-gray-700 bg-white'
	);
	const [secondRowColor, setSecondRowColor] = useState('bg-gray-200');

	const toggleOpen = () => {
		if (slideDownDisabled) {
			return;
		}

		setSlideDownDisabled(true);

		if (open) {
			setTimeout(() => {
				setCellStyle(
					'whitespace-nowrap px-3 py-4 text-sm text-gray-700 bg-white'
				);
				setSecondRowColor('bg-gray-200');
			}, 300);
		} else {
			// state when open
			setCellStyle(
				'whitespace-nowrap px-3 py-4 text-sm text-primary bg-primary-50'
			);
			setSecondRowColor('bg-primary-50');
		}

		setTimeout(() => setSlideDownDisabled(false), 300);

		setOpen(!open);
	};

	return (
		<>
			<tr
				className={`rounded-md relative table-row cursor-pointer`}
				onClick={toggleOpen}
			>
				<td
					className={`${cellStyle} rounded-tl-[7px] ${
						!open && 'rounded-bl-[7px]'
					}`}
				>
					<div
						className={`side-color h-full w-[5px] absolute left-0 top-0 rounded-tl-[10px] bg-primary ${
							open ? 'w-[5px]' : 'w-0'
						}`}
					/>
					{open ? (
						<ChevronUpIcon className="h-6 w-6" />
					) : (
						<ChevronDownIcon className="h-6 w-6" />
					)}
				</td>
				{data.type === 'vehicle-fault' && (
					<>
						<td className={`${cellStyle}`}>{data.registration}</td>
						<td className={`${cellStyle}`}>{data.vehicle_type}</td>
						<td className={`${cellStyle}`}>{data.numberOfFaults}</td>
						<td className={`${cellStyle} capitalize`}>{data.priority}</td>
						<td className={`${cellStyle}`}>
							{new Date(data.submitted_at)
								.toLocaleDateString('en-AU', {
									weekday: 'short',
									day: 'numeric',
									month: 'short',
								})
								.replace(',', '')}
						</td>
						<td className={`${cellStyle}`}>{formatName(data.operator_name)}</td>
						<td className={`${cellStyle}`}>
							{data.branch && data.branch.name}
						</td>
						<td className={`${cellStyle}`}>{data.ticket_number}</td>
						<td
							className={`${cellStyle} rounded-tr-[7px] ${
								!open && 'rounded-br-[7px]'
							}`}
							colSpan={10}
						>
							{<StatusButton status={data.status} />}
						</td>
					</>
				)}

				{data.type === 'incident' && (
					<>
						<td className={`${cellStyle}`}>{data.branch?.name}</td>
						<td className={`${cellStyle}`}>{data.ticket_number}</td>
						<td className={`${cellStyle}`}>
							{new Date(data.submitted_at)
								.toLocaleDateString('en-AU', {
									weekday: 'short',
									day: 'numeric',
									month: 'short',
								})
								.replace(',', '')}
						</td>
						<td className={`${cellStyle} capitalize`}>{data.submitted_by}</td>
						<td className={`${cellStyle}`}>{data.client}</td>
						<td className={`${cellStyle} capitalize`}>{data.severity}</td>
						<td
							className={`${cellStyle} rounded-tr-[7px] ${
								!open && 'rounded-br-[7px]'
							}`}
							colSpan={10}
						>
							{<StatusButton status={data.status} />}
						</td>
					</>
				)}

				{data.type === 'survey' && (
					<>
						<td
							className={`${cellStyle}`}
						>{`${data.first_name} ${data.last_name}`}</td>
						<td className={`${cellStyle}`}>{data?.survey?.name}</td>
						<td className={`${cellStyle}`}>
							{new Date(data?.survey.sent_at)
								.toLocaleDateString('en-AU', {
									weekday: 'short',
									day: 'numeric',
									month: 'short',
								})
								.replace(',', '')}
						</td>
						<td className={`${cellStyle} capitalize`}>{data?.branch?.name}</td>
						<td
							className={`${cellStyle} rounded-tr-[7px] ${
								!open && 'rounded-br-[7px]'
							}`}
							colSpan={10}
						>
							{<StatusButton status={data.survey_status} />}
						</td>
					</>
				)}
			</tr>
			{/* Slide down row */}
			<tr className={`${secondRowColor} second-row`}>
				<td colSpan={10} className="relative">
					<div
						className={`side-color h-full absolute left-0 top-0 rounded-bl-[10px] bg-primary ${
							open ? 'w-[5px]' : 'w-0'
						}`}
					/>
					<SlideDown open={open}>
						<div className="px-4 pb-2 text-left text-black bg-primary-50">
							{data.type === 'vehicle-fault' && <VehicleCard fault={data} />}

							{data.type === 'incident' && <IncidentCard incident={data} />}

							{data.type === 'survey' && <SurveyCard survey={data} />}
						</div>
					</SlideDown>
				</td>
			</tr>
			{/* This row is to allow for grey spacing in table */}
			<tr className="bg-gray-300 h-1" />
		</>
	);
};

export default VehicleRow;
